import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { JAWA_SERVICES } from "@/data/content";
import { EnquiryModal } from "./EnquiryModal";
import { FadeUp, SectionHeading } from "./Reveal";

export const Services = () => {
  const [enquiry, setEnquiry] = useState(null);

  return (
    <section id="services" data-testid="services-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
      <FadeUp className="flex flex-wrap items-end justify-between gap-8">
        <div>
          <SectionHeading overline="JÀWÀ Services" title="Arrive. Settle. Belong." />
          <p className="mt-6 max-w-xl text-base leading-relaxed text-jawa-espresso/70">
            Nine bookable JÀWÀ+ services — plus the full A–Z directory of everything else,
            from accommodation to zen.
          </p>
        </div>
        <Link
          to="/services"
          data-testid="services-view-all"
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-turquoise transition-colors hover:text-jawa-espresso"
        >
          All services & the A–Z directory <ArrowUpRight className="h-4 w-4" />
        </Link>
      </FadeUp>

      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-preview-grid">
        {JAWA_SERVICES.slice(0, 6).map((s, i) => (
          <FadeUp key={s.name} delay={i * 0.05}>
            <article
              data-testid={`service-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group flex h-full cursor-pointer flex-col overflow-hidden border border-jawa-stone bg-white transition-transform duration-300 hover:-translate-y-1"
              onClick={() =>
                s.mode === "enquiry"
                  ? setEnquiry({ journey: "JÀWÀ Services", title: s.name, priceNote: s.price_note })
                  : undefined
              }
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-serif text-xl font-medium tracking-tight text-jawa-espresso">{s.name}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-jawa-espresso/65">{s.text}</p>
                {s.mode === "enquiry" ? (
                  <button
                    onClick={() => setEnquiry({ journey: "JÀWÀ Services", title: s.name, priceNote: s.price_note })}
                    data-testid={`service-open-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="mt-5 inline-flex w-fit items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso transition-colors group-hover:text-jawa-coral"
                  >
                    {s.cta} <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <Link
                    to={s.href}
                    data-testid={`service-open-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="mt-5 inline-flex w-fit items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso transition-colors group-hover:text-jawa-coral"
                  >
                    {s.cta} <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </article>
          </FadeUp>
        ))}
      </div>

      <FadeUp className="mt-14">
        <button
          onClick={() => setEnquiry({ journey: "JÀWÀ Services", title: "General services enquiry" })}
          data-testid="services-enquiry-button"
          className="inline-block bg-jawa-espresso px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors duration-300 hover:bg-jawa-terracotta"
        >
          Ask the Concierge
        </button>
      </FadeUp>

      {enquiry && (
        <EnquiryModal
          journey={enquiry.journey}
          title={enquiry.title}
          variant="service"
          priceNote={enquiry.priceNote}
          onClose={() => setEnquiry(null)}
          testid="home-service-enquiry-modal"
        />
      )}
    </section>
  );
};
